// LifeDrain Effect
// Based on ShadowWraithInkStyle.tsx
// Features: Wraith flies to target, sucks soul particles, moves in oval path

const CONFIG = {
    ragCount: 40,
    hoverSpeed: 1.0,
    mistDensity: 0.4,
    darkness: 0.8,
    scale: 1.6,
    speed: 4.0,
    drainDuration: 4000,
    fadeDuration: 1500
};

// GLOBAL SPRITE CACHE FOR LIFE DRAIN EFFECT
const lifeDrainSpriteCache = new Map<string, HTMLCanvasElement>();

function getBodyMistSprite(size: number, opacityMultiplier: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    // Quantize opacity to reduce cache misses (e.g. nearest 0.05)
    // Actually, maybe better to bake max opacity into sprite and scale via globalAlpha.
    // Original: `rgba(50, 50, 55, ${opacity})`. We can bake `rgba(50, 50, 55, 1)` and use globalAlpha.
    const key = `bodyMist-${qSize}`;
    let sprite = lifeDrainSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        const diameter = qSize * 2;
        sprite.width = diameter + 4;
        sprite.height = diameter + 4;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = sprite.width / 2;
            const centerY = sprite.height / 2;
            const grad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            grad.addColorStop(0, `rgba(50, 50, 55, 1)`); // Max alpha baked in
            grad.addColorStop(1, `rgba(50, 50, 55, 0)`);
            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        lifeDrainSpriteCache.set(key, sprite);
    }
    return sprite;
}

function getFogSprite(size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `fog-${qSize}`;
    let sprite = lifeDrainSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        const diameter = qSize * 2;
        sprite.width = diameter + 4;
        sprite.height = diameter + 4;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = sprite.width / 2;
            const centerY = sprite.height / 2;
            const grad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            grad.addColorStop(0, `rgba(200, 210, 220, 1)`); // Max alpha baked in
            grad.addColorStop(1, `rgba(200, 210, 220, 0)`);
            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        lifeDrainSpriteCache.set(key, sprite);
    }
    return sprite;
}

// --- Classes ---

class Point {
    x: number; y: number; oldX: number; oldY: number; pinned: boolean
    constructor(x: number, y: number, pinned = false) {
        this.x = x; this.y = y; this.oldX = x; this.oldY = y; this.pinned = pinned
    }
    update(drag: number, gravityX: number, gravityY: number) {
        if (this.pinned) return
        const vx = (this.x - this.oldX) * drag
        const vy = (this.y - this.oldY) * drag
        this.oldX = this.x; this.oldY = this.y
        this.x += vx + gravityX
        this.y += vy + gravityY
    }
}

class Constraint {
    p1: Point; p2: Point; length: number; stiffness: number
    constructor(p1: Point, p2: Point, length: number, stiffness = 1) {
        this.p1 = p1; this.p2 = p2; this.length = length; this.stiffness = stiffness
    }
    resolve() {
        const dx = this.p1.x - this.p2.x
        const dy = this.p1.y - this.p2.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < 0.001) return
        const diff = (this.length - dist) / dist * this.stiffness
        if (!this.p1.pinned) { this.p1.x += dx * diff * 0.5; this.p1.y += dy * diff * 0.5 }
        if (!this.p2.pinned) { this.p2.x -= dx * diff * 0.5; this.p2.y -= dy * diff * 0.5 }
    }
}

class Ribbon {
    points: Point[] = []; constraints: Constraint[] = []
    baseWidth: number; layer: number; xOff: number; yOff: number
    noiseOffset: number

    constructor(startX: number, startY: number, xOff: number, yOff: number, segments: number, length: number, width: number, layer: number) {
        this.baseWidth = width; this.layer = layer; this.xOff = xOff; this.yOff = yOff
        this.noiseOffset = Math.random() * 1000
        const segLen = length / segments
        for (let i = 0; i < segments; i++) {
            const px = startX + xOff
            const py = startY + yOff + i * segLen
            const p = new Point(px, py, i === 0)
            this.points.push(p)
            if (i > 0) this.constraints.push(new Constraint(this.points[i - 1], p, segLen, 0.6))
        }
    }

    update(headX: number, headY: number, gravityX: number, gravityY: number) {
        this.points[0].x = headX
        this.points[0].y = headY
        for (let i = 1; i < this.points.length; i++) this.points[i].update(0.92, gravityX, gravityY)
        for (let k = 0; k < 3; k++) this.constraints.forEach(c => c.resolve())
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.points.length < 2) return

        ctx.beginPath()
        const leftSide: { x: number, y: number }[] = []
        const rightSide: { x: number, y: number }[] = []

        for (let i = 0; i < this.points.length; i++) {
            const p = this.points[i]
            let dx = 0, dy = 1
            if (i > 0) { dx = p.x - this.points[i - 1].x; dy = p.y - this.points[i - 1].y }
            else if (i < this.points.length - 1) { dx = this.points[i + 1].x - p.x; dy = this.points[i + 1].y - p.y }

            if (dx === 0 && dy === 0) { dx = 0; dy = 1 }
            let perp = Math.atan2(dy, dx) + Math.PI / 2
            const prog = i / this.points.length

            let currentWidth = this.baseWidth * (1 - Math.pow(prog, 0.5))
            currentWidth *= 0.8 + Math.sin(i * 0.8 + this.noiseOffset) * 0.4

            const wx = Math.cos(perp) * currentWidth
            const wy = Math.sin(perp) * currentWidth
            leftSide.push({ x: p.x - wx, y: p.y - wy })
            rightSide.push({ x: p.x + wx, y: p.y + wy })
        }

        ctx.moveTo(leftSide[0].x, leftSide[0].y)
        for (let i = 1; i < leftSide.length; i++) ctx.lineTo(leftSide[i].x, leftSide[i].y)
        for (let i = rightSide.length - 1; i >= 0; i--) ctx.lineTo(rightSide[i].x, rightSide[i].y)
        ctx.closePath()

        let minY = Infinity, maxY = -Infinity
        this.points.forEach(p => { if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y })

        const g = ctx.createLinearGradient(0, minY, 0, maxY)
        g.addColorStop(0, `rgba(40, 40, 45, 1.0)`)
        g.addColorStop(0.5, `rgba(40, 40, 45, 0.8)`)
        g.addColorStop(1, `rgba(40, 40, 45, 0)`)
        ctx.fillStyle = g
        ctx.fill()
    }
}

class BodyMistParticle {
    x: number; y: number; vx: number; vy: number
    maxLife: number; life: number; size: number

    constructor(startX: number, startY: number, scale: number) {
        this.x = startX + (Math.random() - 0.5) * 15 * scale
        this.y = startY + (Math.random() - 0.5) * 15 * scale
        this.vx = (Math.random() - 0.5) * 0.5
        this.vy = (1.5 + Math.random() * 1.5) * scale
        this.maxLife = 60 + Math.random() * 30; this.life = this.maxLife
        this.size = (10 + Math.random() * 10) * scale
    }
    update() { this.x += this.vx; this.y += this.vy; this.vx *= 0.98; this.size += 0.6; this.life -= 1 }
    draw(ctx: CanvasRenderingContext2D, alphaMultiplier: number = 1) {
        if (this.life <= 0) return
        const opacity = (this.life / this.maxLife) * 0.06 * alphaMultiplier

        ctx.globalAlpha = opacity;
        const sprite = getBodyMistSprite(this.size, opacity);
        const offset = sprite.width / 2;
        ctx.drawImage(sprite, this.x - offset, this.y - offset);
    }
}

class SoulParticle {
    x: number; y: number; vx: number; vy: number
    life: number; size: number

    constructor(startX: number, startY: number) {
        const angle = Math.random() * Math.PI * 2; const dist = Math.random() * 30
        this.x = startX + Math.cos(angle) * dist; this.y = startY + Math.sin(angle) * dist
        this.vx = (Math.random() - 0.5) * 2; this.vy = (Math.random() - 0.5) * 2
        this.life = 1.0; this.size = 2 + Math.random() * 4
    }
    update(targetX: number, targetY: number) {
        const dx = targetX - this.x; const dy = targetY - this.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist > 10) { this.vx += (dx / dist) * 1.5; this.vy += (dy / dist) * 1.5 }
        this.vx *= 0.92; this.vy *= 0.92
        this.x += this.vx; this.y += this.vy; this.life -= 0.01
    }
    draw(ctx: CanvasRenderingContext2D) {
        const alpha = Math.max(0, this.life)
        ctx.globalCompositeOperation = 'source-over'
        ctx.fillStyle = `rgba(180, 100, 255, ${alpha})`
        ctx.shadowBlur = 10; ctx.shadowColor = `rgba(140, 50, 255, ${alpha})`
        ctx.beginPath(); ctx.arc(this.x, this.y, this.size * alpha, 0, Math.PI * 2); ctx.fill()
        ctx.shadowBlur = 0;
    }
}

class FogParticle {
    x: number; y: number; size: number; speed: number; opacity: number
    constructor(w: number, h: number) { this.x = -200; this.y = 0; this.size = 1; this.speed = 1; this.opacity = 0; this.reset(w, h) }
    reset(w: number, h: number) {
        this.x = -200; this.y = Math.random() * h
        this.size = 100 + Math.random() * 300; this.speed = 0.5 + Math.random() * 1.5
        this.opacity = 0.05 + Math.random() * 0.15
    }
    update(w: number, h: number) { this.x += this.speed; if (this.x > w + 200) this.reset(w, h) }
    draw(ctx: CanvasRenderingContext2D, alphaMultiplier: number = 1) {
        ctx.globalAlpha = this.opacity * alphaMultiplier;
        const sprite = getFogSprite(this.size);
        const offset = sprite.width / 2;
        ctx.drawImage(sprite, this.x - offset, this.y - offset);
    }
}

export type LifeDrainEffect = {
    kind: 'lifeDrain'
    t: number
    sx: number; sy: number
    tx: number; ty: number
    data?: {
        ribbons: Ribbon[]
        fog: FogParticle[]
        bodyMist: BodyMistParticle[]
        souls: SoulParticle[]
        pos: { x: number, y: number }
        prevPos: { x: number, y: number }
        vel: { x: number, y: number }
        phase: 'APPROACHING' | 'DRAINING'
        drainTime: number
        initialDist: number
        loopStartPos?: { x: number, y: number }
    }
}

export const createLifeDrainEffect = (sx: number, sy: number, tx: number, ty: number): LifeDrainEffect => ({
    kind: 'lifeDrain', t: 0, sx, sy, tx, ty
})

export const renderLifeDrainEffect = (
    eff: LifeDrainEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    const { width, height } = ctx.canvas
    const cx = width / 2, cy = height / 2
    const isTopPlayer = eff.ty < cy;

    if (!eff.data) {
        const spawnX = -100
        const spawnY = eff.ty; // Spawn near their Y level

        const dx = eff.tx - spawnX
        const dy = eff.ty - spawnY
        const initialDist = Math.sqrt(dx * dx + dy * dy)

        const ribbons: Ribbon[] = []
        for (let i = 0; i < CONFIG.ragCount; i++) {
            const angle = (i / CONFIG.ragCount) * Math.PI * 2
            const xOff = Math.cos(angle) * 40 * CONFIG.scale
            const yOff = Math.sin(angle) * 10 * CONFIG.scale
            const isFront = Math.sin(angle) > 0
            const r = new Ribbon(spawnX, spawnY, xOff, yOff, 20, (150 + Math.random() * 200) * CONFIG.scale, (10 + Math.random() * 10) * CONFIG.scale, isFront ? 1 : 0)
            ribbons.push(r)
        }
        ribbons.sort((a, b) => a.layer - b.layer)

        eff.data = {
            ribbons,
            fog: Array.from({ length: 15 }, () => new FogParticle(width, height)),
            bodyMist: [],
            souls: [],
            pos: { x: spawnX, y: spawnY },
            prevPos: { x: spawnX - 1, y: spawnY },
            vel: { x: CONFIG.speed, y: 0 },
            phase: 'APPROACHING',
            drainTime: 0,
            initialDist
        }
    }

    const d = eff.data
    const df = (eff as any)._df ?? 1
    const time = Date.now() * 0.001

    let globalAlpha = 1.0
    if (eff.t < 1500) globalAlpha = eff.t / 1500
    if (d.drainTime > CONFIG.drainDuration) {
        globalAlpha = Math.min(globalAlpha, 1 - Math.pow(Math.min(1, (d.drainTime - CONFIG.drainDuration) / CONFIG.fadeDuration), 2))
        if (globalAlpha <= 0) return false
    }

    d.prevPos.x = d.pos.x; d.prevPos.y = d.pos.y
    const dx = eff.tx - d.pos.x; const dy = eff.ty - d.pos.y
    let gx = 0, gy = 0.2

    if (d.phase === 'APPROACHING') {
        const approachTargetX = isTopPlayer ? cx * 0.6 : cx * 0.4;
        const approachTargetY = isTopPlayer ? eff.ty + 150 : eff.ty - 160;
        const adx = approachTargetX - d.pos.x
        const ady = approachTargetY - d.pos.y
        const dist = Math.sqrt(adx * adx + ady * ady)

        if (dist > 1) {
            const targetVelX = (adx / dist) * CONFIG.speed
            const targetVelY = (ady / dist) * CONFIG.speed
            d.vel.x += (targetVelX - d.vel.x) * 0.05
            d.vel.y += (targetVelY - d.vel.y) * 0.05
        }
        d.pos.x += d.vel.x
        d.pos.y += d.vel.y

        if (dist < 50) {
            d.phase = 'DRAINING'
            d.loopStartPos = { x: d.pos.x, y: d.pos.y }
        }
    } else if (d.phase === 'DRAINING') {
        d.drainTime += 16.7 * df
        const progress = Math.min(1, d.drainTime / CONFIG.drainDuration)

        const loopW = width * 0.35
        const loopH = height * 0.15
        const orbitCenterX = isTopPlayer ? cx + loopW * 0.2 : cx - loopW * 0.7;
        const orbitCenterY = isTopPlayer ? eff.ty + 150 : eff.ty - 160;

        const pathT = Math.PI - (progress * Math.PI * 1.5)

        const targetX = orbitCenterX + Math.cos(pathT) * loopW
        const targetY = orbitCenterY + Math.sin(pathT) * loopH

        const toTargetX = targetX - d.pos.x
        const toTargetY = targetY - d.pos.y
        const toTargetDist = Math.sqrt(toTargetX * toTargetX + toTargetY * toTargetY)

        if (toTargetDist > 1) {
            const targetVelX = (toTargetX / toTargetDist) * CONFIG.speed
            const targetVelY = (toTargetY / toTargetDist) * CONFIG.speed
            d.vel.x += (targetVelX - d.vel.x) * 0.04
            d.vel.y += (targetVelY - d.vel.y) * 0.04
        }

        d.pos.x += d.vel.x
        d.pos.y += d.vel.y

        if (d.drainTime < CONFIG.drainDuration - 500) {
            if (Math.random() < 0.9) d.souls.push(new SoulParticle(eff.tx, eff.ty))
            if (Math.random() < 0.5) d.souls.push(new SoulParticle(eff.tx, eff.ty))
        }
    }

    const heading = 0.0

    d.fog.forEach(f => f.update(width, height))
    for (let k = 0; k < Math.floor(CONFIG.mistDensity * 3) + 1; k++) d.bodyMist.push(new BodyMistParticle(d.pos.x, d.pos.y, CONFIG.scale))

    for (let i = d.bodyMist.length - 1; i >= 0; i--) {
        d.bodyMist[i].update();
        if (d.bodyMist[i].life <= 0) {
            d.bodyMist[i] = d.bodyMist[d.bodyMist.length - 1];
            d.bodyMist.pop();
        }
    }

    d.ribbons.forEach(r => {
        const sway = d.phase === 'DRAINING' ? Math.sin(time * 5 + r.noiseOffset) * 2 : Math.sin(time + r.noiseOffset) * 5
        r.update(d.pos.x + r.xOff + sway, d.pos.y + r.yOff, gx, gy)
    })

    const mouthX = d.pos.x + 15 * Math.sin(heading)
    const mouthY = d.pos.y - 15 * Math.cos(heading)
    for (let i = d.souls.length - 1; i >= 0; i--) {
        d.souls[i].update(mouthX, mouthY)
        if (d.souls[i].life <= 0 || Math.hypot(d.souls[i].x - mouthX, d.souls[i].y - mouthY) < 10) {
            d.souls[i] = d.souls[d.souls.length - 1];
            d.souls.pop();
        }
    }

    // --- RENDER ---
    ctx.save()

    let darkAlpha = CONFIG.darkness * globalAlpha
    if (darkAlpha > 0.01) {
        const g = ctx.createRadialGradient(cx, cy, height * 0.4, cx, cy, height * 0.9)
        g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, `rgba(0,0,0,${darkAlpha})`)
        ctx.fillStyle = g; ctx.fillRect(0, 0, width, height)

        const gl = ctx.createLinearGradient(0, 0, width * 0.3, 0)
        gl.addColorStop(0, `rgba(0,0,0,${darkAlpha})`)
        gl.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.fillStyle = gl; ctx.fillRect(0, 0, width * 0.3, height)

        const gr = ctx.createLinearGradient(width, 0, width * 0.7, 0)
        gr.addColorStop(0, `rgba(0,0,0,${darkAlpha})`)
        gr.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.fillStyle = gr; ctx.fillRect(width * 0.7, 0, width * 0.3, height)
    }

    ctx.globalAlpha = globalAlpha

    d.fog.forEach(f => f.draw(ctx, globalAlpha))
    ctx.globalCompositeOperation = 'source-over'
    d.bodyMist.forEach(p => p.draw(ctx, globalAlpha))
    ctx.shadowBlur = 15; ctx.shadowColor = 'rgba(0,0,0,0.5)'
    d.ribbons.forEach(r => r.draw(ctx))

    ctx.save()
    ctx.translate(d.pos.x, d.pos.y)
    ctx.rotate(heading)
    ctx.translate(0, -15)

    ctx.scale(CONFIG.scale * 0.63, CONFIG.scale * 0.63)

    ctx.shadowBlur = 40; ctx.shadowColor = 'black'
    ctx.fillStyle = 'rgba(20, 20, 25, 1.0)'
    ctx.beginPath()
    const hoodW = 55; const hoodH = 70
    ctx.moveTo(-hoodW * 0.6, 40)
    ctx.bezierCurveTo(-hoodW, 0, -hoodW * 0.6, -hoodH, 0, -hoodH - 5)
    ctx.bezierCurveTo(hoodW * 0.6, -hoodH, hoodW, 0, hoodW * 0.6, 40)
    ctx.lineTo(hoodW * 0.3, 30); ctx.lineTo(0, 60); ctx.lineTo(-hoodW * 0.3, 30)
    ctx.closePath(); ctx.fill()
    const faceY = -15
    const fg = ctx.createRadialGradient(0, faceY, 0, 0, faceY, 30)
    fg.addColorStop(0, '#000000'); fg.addColorStop(0.5, 'rgba(0,0,0,0.8)'); fg.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = fg; ctx.beginPath(); ctx.arc(0, faceY, 25, 0, Math.PI * 2); ctx.fill()
    ctx.restore()

    d.souls.forEach(s => s.draw(ctx))

    ctx.restore()
    return true
}
